import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, RefreshCw } from 'lucide-react';
import { importSiniestralidad, deleteSiniestPorAno } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export function ImportPanel({ onImported }) {
  const [dragging, setDragging] = useState(false);
  const [selectedAno, setSelectedAno] = useState(new Date().getFullYear());
  const fileRef = useRef();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const b64 = result.includes(',') ? result.split(',')[1] : result;
          resolve(importSiniestralidad({ filename: file.name, file_base64: b64, ano: Number(selectedAno) }));
        };
        reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
        reader.readAsDataURL(file);
      });
    },
    onSuccess: (data) => {
      const v = data.verificacion;
      const verMsg = v ? ` · ACC: ${v.acc_con_baja} | JP: ${v.jornadas_perdidas} | IF: ${v.if_global} | IG: ${v.ig_global}` : '';
      toast.success(`Año ${data.ano}: ${data.kpi_centros} centros, ${data.incidentes} incidentes${verMsg}`, {
        duration: 10000,
        description: 'Verifica estos totales contra tu Excel. Si no coinciden, revisa los centros sin código CT.',
      });
      qc.invalidateQueries({ queryKey: ['siniest'] });
      if (onImported) onImported();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Error al importar');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSiniestPorAno(selectedAno),
    onSuccess: (data) => {
      toast.success(`Datos de ${selectedAno} eliminados (${data.deleted_kpis} centros, ${data.deleted_propios} incidentes)`);
      qc.invalidateQueries({ queryKey: ['siniest'] });
    },
    onError: () => toast.error('Error al eliminar los datos'),
  });

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i)) { toast.error('Solo archivos Excel (.xlsx)'); return; }
    mutation.mutate(file);
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear];

  return (
    <div className="space-y-3">
      {/* Year selector */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-xs font-medium" style={{ color: 'var(--vz-text-secondary)' }}>Año del Excel:</span>
        <div className="flex gap-1">
          {years.map(y => (
            <button
              key={y}
              onClick={() => setSelectedAno(y)}
              className="px-3 py-1 text-xs rounded-lg font-semibold transition-all"
              style={{
                background: selectedAno === y ? 'var(--vz-green)' : 'rgba(74,158,255,0.08)',
                color: selectedAno === y ? '#fff' : 'var(--vz-text-secondary)',
                border: `1px solid ${selectedAno === y ? 'var(--vz-green)' : 'rgba(74,158,255,0.2)'}`,
              }}
              data-testid={`siniest-ano-btn-${y}`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Drop zone */}
      <div
        className={`rounded-xl border-2 border-dashed p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors duration-200 ${dragging ? 'border-green-400' : ''}`}
        style={{
          borderColor: dragging ? 'var(--vz-green)' : 'var(--vz-border-subtle)',
          background: dragging ? 'rgba(0,153,64,0.04)' : 'var(--vz-bg-elevated)',
        }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; handleFile(f); }}
        onClick={() => fileRef.current?.click()}
        data-testid="siniest-import-zone"
      >
        <input type="file" accept=".xlsx,.xls" ref={fileRef} className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
        {mutation.isPending
          ? (
            <>
              <RefreshCw size={28} className="animate-spin" style={{ color: 'var(--vz-green)' }} />
              <p className="text-sm" style={{ color: 'var(--vz-text-muted)' }}>Importando año {selectedAno}...</p>
            </>
          ) : (
            <>
              <Upload size={28} style={{ color: 'var(--vz-green)' }} />
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--vz-text-primary)' }}>Arrastrar o hacer clic para importar</p>
                <p className="text-xs mt-1" style={{ color: 'var(--vz-text-muted)' }}>
                  Excel con 2 hojas: INCIDENTES LABORALES + Incidentes PROPIOS · Año <strong>{selectedAno}</strong>
                </p>
                <Badge className="mt-2 text-[10px]" style={{ background: 'rgba(0,153,64,0.1)', color: 'var(--vz-green)', border: '1px solid rgba(0,153,64,0.2)' }}>
                  Sustituye todos los datos del año {selectedAno}
                </Badge>
              </div>
            </>
          )
        }
      </div>

      {/* Danger zone: delete year data */}
      <div className="mt-3 pt-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,50,50,0.2)' }}>
        <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Borrar todos los datos importados del año {selectedAno}:</span>
        <button
          onClick={() => {
            if (window.confirm(`¿Eliminar TODOS los datos de Siniestralidad del año ${selectedAno}? Esta acción no se puede deshacer.`)) {
              deleteMutation.mutate();
            }
          }}
          disabled={deleteMutation.isPending}
          data-testid="siniest-delete-ano-btn"
          className="text-[10px] px-2 py-1 rounded"
          style={{ background: 'rgba(255,50,50,0.1)', color: '#FF5252', border: '1px solid rgba(255,50,50,0.3)', cursor: 'pointer' }}
        >
          {deleteMutation.isPending ? 'Eliminando...' : `Borrar ${selectedAno}`}
        </button>
      </div>
    </div>
  );
}
